import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Maximize2 } from 'lucide-react';
import { CardWithHeader } from '../ui/CardWithHeader';
import { ExpandModal } from '../ui/ExpandModal';
import { NoDataInPeriod } from '../ui/NoDataInPeriod';
import { RevenueMix } from '../../utils/revenueHistoryCalculations';

export interface RestaurantMixRow {
  restaurantId: string;
  restaurantName: string;
  mix: RevenueMix;
}

interface RestaurantRevenueMixCardProps {
  /** Per-restaurant food/drinks split, sorted by revenue desc. */
  items: RestaurantMixRow[];
  /** Overall (group-wide) food/drinks split for the header legend. */
  overall: RevenueMix;
  loading?: boolean;
}

const PREVIEW_MAX_HEIGHT = 200;
const foodColor = '#18B081';
const drinksColor = '#F4B857';

/** Name on the left, a single segmented food/drinks bar with the %% rendered inside each segment. */
const MixRow: React.FC<{ row: RestaurantMixRow }> = ({ row }) => {
  const food = row.mix.food.percentage;
  const drinks = row.mix.drinks.percentage;
  return (
    <div className="flex items-center gap-3">
      <span className="text-[15px] leading-5 font-medium text-textPrimary dark:text-textPrimary-dark w-[70px] flex-shrink-0 truncate">
        {row.restaurantName}
      </span>
      <div className="flex-1 flex h-[20px] rounded-lg overflow-hidden bg-[#F2F4F7] dark:bg-gray-700">
        {food > 0 && (
          <div
            className="flex items-center justify-center text-white dark:text-white text-[12px] leading-5 font-semibold overflow-hidden whitespace-nowrap"
            style={{ width: `${food}%`, backgroundColor: foodColor }}
          >
            {food >= 16 ? `${food.toFixed(0)}%` : ''}
          </div>
        )}
        {drinks > 0 && (
          <div
            className="flex items-center justify-center text-white dark:text-white text-[12px] leading-5 font-semibold overflow-hidden whitespace-nowrap"
            style={{ width: `${drinks}%`, backgroundColor: drinksColor }}
          >
            {drinks >= 12 ? `${drinks.toFixed(0)}%` : ''}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * All-restaurants "Food vs drink mix" card: per-restaurant food vs drinks revenue split,
 * matching the new UI (name left + inline segmented bar with the %% inside each segment).
 * The header shows the overall split; maximize opens the full list.
 */
export const RestaurantRevenueMixCard: React.FC<RestaurantRevenueMixCardProps> = ({
  items,
  overall,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const maximizeButton = (
    <button
      type="button"
      onClick={() => setIsModalOpen(true)}
      title={t('dashboard.maximize')}
      aria-label={t('dashboard.maximize')}
      className="p-1.5 rounded-md text-textSecondary dark:text-textSecondary-dark hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      <Maximize2 className="w-4 h-4" />
    </button>
  );

  const legend = (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: foodColor }} />
        <span className="text-[13px] text-textSecondary dark:text-textSecondary-dark">
          {t('revenueHistory.food')}:{' '}
          <strong className="text-textPrimary dark:text-textPrimary-dark">{overall.food.percentage.toFixed(0)}%</strong>
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: drinksColor }} />
        <span className="text-[13px] text-textSecondary dark:text-textSecondary-dark">
          {t('revenueHistory.drinks')}:{' '}
          <strong className="text-textPrimary dark:text-textPrimary-dark">{overall.drinks.percentage.toFixed(0)}%</strong>
        </span>
      </div>
    </div>
  );

  const renderList = (rows: RestaurantMixRow[], scroll: boolean) => {
    if (loading) {
      return (
        <div className="flex flex-col gap-4 py-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-5 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          ))}
        </div>
      );
    }
    if (rows.length === 0) {
      return <NoDataInPeriod />;
    }
    return (
      <div
        className={'w-full flex flex-col gap-4 pr-1' + (scroll ? ' overflow-y-auto custom-scroll' : '')}
        style={scroll ? { maxHeight: PREVIEW_MAX_HEIGHT } : undefined}
      >
        {rows.map(row => (
          <MixRow key={row.restaurantId} row={row} />
        ))}
      </div>
    );
  };

  return (
    <>
      <CardWithHeader
        title={t('revenueHistory.foodDrinkMix')}
        headerActions={legend}
        headerActionsRight={maximizeButton}
      >
        {renderList(items, true)}
      </CardWithHeader>

      <ExpandModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('revenueHistory.foodDrinkMix')}
        maxWidth="max-w-2xl"
      >
        {renderList(items, false)}
      </ExpandModal>
    </>
  );
};

export default RestaurantRevenueMixCard;
